import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone } from 'lucide-react';
import type { SessionSummary } from '@nbh/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';

/**
 * The devices that can reach this account, and a way to end one (decision D41, R3).
 *
 * This is the half of DEBT-7 no interface change could reach. Sign-out was already one
 * tap with the signed-in name permanently on screen, and the debt survived both — because
 * a phone left on a charging shelf, or lost off-site, is not somewhere anybody is going to
 * tap anything. Until this screen existed the only lever was `User.isActive`, which signs
 * a person out of every device at once.
 */
export function SessionList() {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const [rows, setRows] = useState<SessionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .listSessions()
      .then((next) => {
        setRows(next);
        setError(null);
      })
      .catch(() => setError(t('settings.sessions.loadFailed')));
  }, [t]);

  useEffect(load, [load]);

  /**
   * Ending the session you are holding signs you out, so it runs the full sign-out —
   * clearing the cookie, the cached profile and the caches — rather than leaving the app
   * running against a credential the server has just refused.
   */
  const revoke = useCallback(
    async (session: SessionSummary) => {
      setBusy(session.id);
      try {
        await api.revokeSession(session.id);
        if (session.current) await signOut();
        else load();
      } catch {
        setError(t('settings.sessions.revokeFailed'));
      } finally {
        setBusy(null);
      }
    },
    [load, signOut, t],
  );

  if (error && !rows) {
    return (
      <p role="alert" className="px-4 py-3 text-sm text-red-400">
        {error}
      </p>
    );
  }
  if (!rows) return <p className="px-4 py-3 text-sm text-slate-500">{t('settings.loading')}</p>;

  return (
    <div>
      {error && (
        <p role="alert" className="px-4 pb-2 text-sm text-red-400">
          {error}
        </p>
      )}
      <ul className="divide-y divide-slate-800">
        {rows.map((session) => (
          <SessionRow
            key={session.id}
            session={session}
            busy={busy === session.id}
            onRevoke={() => void revoke(session)}
          />
        ))}
      </ul>
    </div>
  );
}

function SessionRow({
  session,
  busy,
  onRevoke,
}: {
  session: SessionSummary;
  busy: boolean;
  onRevoke: () => void;
}) {
  const { t } = useTranslation();

  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <Smartphone className="mt-0.5 size-4 shrink-0 text-slate-500" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-100">
          {session.deviceName ?? t('settings.sessions.unknownDevice')}
          {/* Marked by the server: the cookie is httpOnly, so the browser cannot work out
              which of these rows is itself. Ending this one signs you out. */}
          {session.current && (
            <span className="rounded-full bg-brookfields-green px-2 py-0.5 text-[11px] font-semibold text-white ring-1 ring-brookfields-gold/70">
              {t('settings.sessions.thisDevice')}
            </span>
          )}
        </p>
        <p className="mt-0.5 text-xs text-slate-400">
          {t('settings.sessions.lastSeen', { at: formatDateTime(session.lastSeenAt) })}
        </p>
        <p className="text-xs text-slate-600">
          {t('settings.sessions.signedInAt', { at: formatDateTime(session.createdAt) })}
        </p>
      </div>
      <button
        type="button"
        onClick={onRevoke}
        disabled={busy}
        className="min-h-[48px] shrink-0 rounded-lg border border-slate-700 bg-transparent px-3 text-sm font-medium text-slate-200 disabled:opacity-50"
      >
        {session.current ? t('settings.sessions.endThis') : t('settings.sessions.end')}
      </button>
    </li>
  );
}
